/**
 * Turns pushed i16 PCM (from the native system-loopback tap, bridged over a
 * Tauri Channel) into a `MediaStream` the existing MediaRecorder path can
 * encode to webm/opus — so the backend's `?channel=remote` stream looks
 * identical to the mic channel.
 *
 * Uses an AudioWorklet (WebKit-compatible; `MediaStreamTrackGenerator` isn't in
 * the macOS WKWebView Tauri uses) feeding a `MediaStreamAudioDestinationNode`.
 *
 * Rate-robust: the worklet plays samples at the AudioContext's actual rate. We
 * request the input rate (16 kHz) but some webviews ignore it and run at the
 * hardware rate (e.g. 48 kHz) — pushing 16 kHz samples into a 48 kHz context
 * would play them 3× too fast and wreck transcription. So we resample the
 * pushed PCM to whatever rate the context actually chose (a no-op when honored).
 */

const WORKLET_URL = "/pcm-player.worklet.js"

export interface IRemotePcmSink {
  /** The audio stream a MediaRecorder records to webm/opus. */
  stream: MediaStream
  /** Push a chunk of little-endian mono i16 samples (at the input rate). */
  push: (pcm: Int16Array) => void
  /** Tear down the worklet + AudioContext. */
  close: () => Promise<void>
}

/**
 * Build a remote-PCM sink. `inputRate` must match the native tap output (16 kHz).
 */
export async function createRemotePcmSink(inputRate = 16_000): Promise<IRemotePcmSink> {
  const ctx = new AudioContext({ sampleRate: inputRate })
  // A capture-only graph (worklet → MediaStreamDestination, no speakers) still
  // needs the context running to pull process().
  if (ctx.state === "suspended") await ctx.resume()
  const ctxRate = ctx.sampleRate
  console.info(`[remote-capture] AudioContext sampleRate=${ctxRate} (requested ${inputRate})`)
  await ctx.audioWorklet.addModule(WORKLET_URL)

  const node = new AudioWorkletNode(ctx, "pcm-player", {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
  })
  const dest = ctx.createMediaStreamDestination()
  node.connect(dest)

  // Stateful linear resampler: inputRate → ctxRate (ratio 1 ⇒ passthrough).
  const ratio = ctxRate / inputRate
  let pos = 0
  let prev = 0
  let primed = false

  return {
    stream: dest.stream,
    push: (pcm: Int16Array): void => {
      if (pcm.length === 0) return
      const inF = new Float32Array(pcm.length)
      for (let i = 0; i < pcm.length; i++) inF[i] = pcm[i] / 32_768

      if (ratio === 1) {
        node.port.postMessage(inF, [inF.buffer])
        return
      }

      if (!primed) {
        prev = inF[0]
        primed = true
      }
      const step = 1 / ratio
      const out = new Float32Array(Math.ceil(inF.length * ratio) + 2)
      let n = 0
      while (pos < inF.length - 1) {
        const idx = Math.floor(pos)
        const frac = pos - idx
        const a = idx < 0 ? prev : inF[idx]
        const b = inF[idx + 1]
        out[n++] = a + (b - a) * frac
        pos += step
      }
      prev = inF[inF.length - 1]
      pos -= inF.length
      const chunk = out.slice(0, n)
      node.port.postMessage(chunk, [chunk.buffer])
    },
    close: async (): Promise<void> => {
      try {
        node.disconnect()
      } catch {
        // already torn down
      }
      try {
        await ctx.close()
      } catch {
        // already closed
      }
    },
  }
}
